// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
// Material
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
// RXJS
import { distinctUntilChanged, filter, skip, tap } from 'rxjs/operators';
import { merge } from 'rxjs';
// NGRX
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../../../../../core/reducers';
// CRUD
import {
  LayoutUtilsService,
  MessageType,
  QueryParamsModel,
} from '../../../../../../../../core/_base/crud';
// Services and models
import {
  ClearContactRelationshipState,
  ContactModel,
  ContactRelationshipActionTypes,
  ContactRelationshipModel,
  ContactRelationshipOnServerCreated,
  ContactRelationshipOnServerUpdated,
  ContactRelationshipsDataSource,
  ContactRelationshipsPageRequested,
  OneContactRelationshipOnServerDeleted,
  selectContactRelationshipErrorState,
  selectContactRelationshipLatestSuccessfullAction,
  selectContactRelationshipsActionLoading,
  selectContactRelationshipsPageLastQuery,
} from '../../../../../../../../core/lists';
// Components
import { RelationshipEditDialogComponent } from '../relationship-edit/relationship-edit-dialog.component';
import { RelationshipAgreementsDialogComponent } from '../relationship-agreements/relationship-agreements-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { FormBuilder } from '@angular/forms';
import { ApiActionTypes } from '../../../../../../../../core/events';
import { ActivatedRoute } from '@angular/router';
import { GeneralValidationMessagesService } from '../../../../../../../../core/services/general-validation-messages.service';
import { Update } from '@ngrx/entity';
import { AgreementService } from 'src/app/core/agreement/_services';
import { currentUser } from 'src/app/core/auth/_selectors/auth.selectors';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-relationships-list',
  templateUrl: './relationships-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RelationshipsListComponent implements OnInit, OnDestroy {
  contactId;
  isViewMode: boolean;
  @Input() contact: ContactModel;
  lastQuery: QueryParamsModel;
  viewLoading = false;
  dataSource: ContactRelationshipsDataSource;
  displayedColumns = [
    'name',
    'relationType.category.entityType',
    'relationType.category.name',
    'relationType.descriptionType',
    'classification',
    'actions',
  ];
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('sort', { static: true }) sort: MatSort;
  selection = new SelectionModel<ContactRelationshipModel>(true, []);
  contactRelationshipsResult: ContactRelationshipModel[] = [];
  isSwitchedToEditMode = false;
  loadingAfterSubmit = false;
  relForEdit: ContactRelationshipModel;
  private subscriptions: any[] = [];
  loading = true;
  filterStatus = '';
  companyId: number;

  constructor(
    private store: Store<AppState>,
    private fb: FormBuilder,
    public dialog: MatDialog,
    private translate: TranslateService,
    private layoutUtilsService: LayoutUtilsService,
    private activateRoute: ActivatedRoute,
    private globalService: GeneralValidationMessagesService,
    private cdr: ChangeDetectorRef,
    private agreementService: AgreementService,
  ) {
    this.store
      .pipe(
        select(currentUser),
        filter((res) => !!res.id),
      )
      .subscribe((res) => {
        this.companyId = res.companyID;
      });
  }

  ngOnInit() {
    // If the user changes the sort order, reset back to the first page.
    const sortSubscription = this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = 0;
      this.sort.active = this.sort.direction ? this.sort.active : 'name';
      this.sort.direction = this.sort.direction || 'desc';
    });
    this.subscriptions.push({ id: 1, sub: sortSubscription });
    const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page)
      .pipe(tap(() => this.loadRelsList()))
      .subscribe();
    this.subscriptions.push({ id: 2, sub: paginatorSubscriptions });

    const errorSubscription = this.store
      .pipe(select(selectContactRelationshipErrorState), skip(1))
      .subscribe((res) => {
        if (res) {
          this.layoutUtilsService.showActionNotification(
            this.translate.instant('GLOBAL.UNSPECIFIED_ERROR'),
            MessageType.Delete,
            3000,
            true,
            false,
          );
        }
      });
    this.subscriptions.push({ id: 3, sub: errorSubscription });

    const successSubscription = this.store
      .pipe(select(selectContactRelationshipLatestSuccessfullAction), skip(1))
      .subscribe((res) => {
        if (res && res.action_type) {
          let successMsg;
          let msgType;
          switch (res.action_type) {
            case ContactRelationshipActionTypes.ContactRelationshipCreated:
              successMsg = this.translate.instant('CONTACT_RELATIONSHIP.EDIT.ADD_MESSAGE');
              msgType = MessageType.Create;
              break;
            case ApiActionTypes.ApiUpdated:
              successMsg = this.translate.instant('CONTACT_RELATIONSHIP.EDIT.UPDATE_MESSAGE');
              msgType = MessageType.Update;
              break;
          }
          this.layoutUtilsService.showActionNotification(successMsg, msgType, 3000, true, false);
        }
      });
    this.subscriptions.push({ id: 4, sub: successSubscription });
    if (this.contact) {
      this.contactId = this.contact.id;
      this.loadRelsList();
    }
    // Init DataSource
    this.dataSource = new ContactRelationshipsDataSource(this.store);
    const entitiesSubscription = this.dataSource.entitySubject
      .pipe(skip(1), distinctUntilChanged())
      .subscribe((res) => {
        this.contactRelationshipsResult = res;
        this.loading = false;
      });
    this.subscriptions.push({ id: 5, sub: entitiesSubscription });
    const lastQuerySubscription = this.store
      .pipe(select(selectContactRelationshipsPageLastQuery))
      .subscribe((res) => (this.lastQuery = res));
    // Load last query from store
    this.subscriptions.push({ id: 6, sub: lastQuerySubscription });
    const loadingSub = this.store
      .pipe(select(selectContactRelationshipsActionLoading))
      .subscribe((res) => (this.viewLoading = res));
    this.subscriptions.push({ id: 7, sub: loadingSub });
    this.isViewMode = this.activateRoute.url[`_value`][1]?.path === 'view';
    this.displayedColumns = this.isViewMode
      ? [
          'name',
          'relationType.category.entityType',
          'relationType.category.name',
          'relationType.descriptionType',
          'classification',
        ]
      : [
          'name',
          'relationType.category.entityType',
          'relationType.category.name',
          'relationType.descriptionType',
          'classification',
          'actions',
        ];
    this.activateRoute.params.subscribe((params) => {
      if (params?.id)
        this.globalService.getEntitiesById('contacts', params.id).subscribe((res) => {
          if (res) {
            this.contact = res.data;
            this.contactId = res.data?.id;
            this.loadRelsList();
          }
        });
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((el) => {
      if (el.sub) {
        el.sub.unsubscribe();
      }
    });
    this.store.dispatch(new ClearContactRelationshipState());
  }

  loadRelsList() {
    this.selection.clear();
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(this.sort.direction, this.sort.active),
      this.sort.direction ? this.sort.direction : 'asc',
      this.sort.active && this.sort.active !== 'name' ? this.sort.active : 'entity.firstName',
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );
    // Call request from server
    this.store.dispatch(
      new ContactRelationshipsPageRequested({
        page: queryParams,
        contactId: this.contactId,
        type: this.filterStatus,
        fromwhere: 'Cont',
        // entityType: this.filterStatus
      }),
    );
  }

  /**
   * Retirns object for filter
   */
  filterConfiguration(order, direction): any {
    const filter: any = {};
    const o = order && order !== '' ? order : 'asc';
    if (direction === '' || direction === 'name') {
      filter.sort = `entity.lastName,${o}`;
    }
    return filter;
  }

  deleteRel(_item: ContactRelationshipModel) {
    const _title = 'Relationship Delete';
    const _description = 'Are you sure to permanently delete this relationship?';
    const _waitDesciption = 'Relationship is deleting...';
    const _deleteMessage = `Relationship has been deleted`;

    const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDesciption);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      this.store.dispatch(
        new OneContactRelationshipOnServerDeleted({
          contactRelationship: _item,
          contact: this.contact,
        }),
      );
      this.loadRelsList();
      this.layoutUtilsService.showActionNotification(
        _deleteMessage,
        MessageType.Delete,
        3000,
        true,
        false,
      );
    });
  }

  /**
   * Open add rel dialog and save data
   */
  addRel() {
    // tslint:disable-next-line:prefer-const
    const newRel = new ContactRelationshipModel();
    newRel.clear();
    const dialogRef = this.dialog.open(RelationshipEditDialogComponent, {
      data: {
        contactEntity: false,
        contact_id: this.contactId,
        contact: this.contact,
      },
      width: '800px',
    });
    dialogRef.afterClosed().subscribe((res) => {
      if (res && res.done) {
        newRel.id = res.response.entity_id;
        newRel.entity_id = res.response.entity_id;
        newRel.entity_type = res.response.entity_type;
        newRel.relationship_description = res.response.relationship_description;
        newRel.relation_type = res.response.relation_type;
        newRel.relation_type_id = res.response.relation_type_id;
        newRel.is_prime = res.response.is_prime;
        newRel.title = res.response.title;
        newRel.emergency_contact = res.response.emergency_contact;
        newRel.financial_responsible = res.response.financial_responsible;
        newRel.phi_authorized = res.response.phi_authorized;
        newRel.valid_until_revoked = res.response.valid_until_revoked;
        newRel.authorized_upto_date = res.response.authorized_upto_date;
        this.store.dispatch(
          new ContactRelationshipOnServerCreated({
            contactRelationship: newRel,
            contact: this.contact,
            type: 'contact',
            agreement: res.response.agreement.data,
          }),
        );
        this.loadRelsList();
        // create the agreement here.
        const successMsg = this.translate.instant('CONTACT_RELATIONSHIP.EDIT.ADD_MESSAGE');
        this.layoutUtilsService.showActionNotification(
          successMsg,
          MessageType.Create,
          3000,
          true,
          false,
        );
      }
    });
  }

  getFullName(obj) {
    const first = obj && obj.first_name ? obj.first_name : '';
    const last = obj && obj.last_name ? obj.last_name : '';
    return first + ' ' + last;
  }

  /**
   * Update relationship
   *
   * @param _item: ContactRelationshipModel
   */
  editRelationshipButtonOnClick(_item: ContactRelationshipModel) {
    const dialogRef = this.dialog.open(RelationshipEditDialogComponent, {
      data: {
        contact: this.contact,
        item: _item,
        contactEntity: false,
        contact_id: this.contactId,
      },
      width: '800px',
    });
    dialogRef.afterClosed().subscribe((res) => {
      const newRel = new ContactRelationshipModel();
      newRel.clear();
      if (res && res.done) {
        newRel.id = _item.id;
        newRel.first_name = _item.first_name;
        newRel.last_name = _item.last_name;
        newRel.created = _item.created;
        newRel.contact_id = this.contact.id;
        newRel.entity_id = res.response.entity_id;
        newRel.entity_type = res.response.entity_type;
        newRel.relationship_description = res.response.relationship_description;
        newRel.relation_type = res.response.relation_type.name;
        newRel.relation_type_id = res.response.relation_type_id;
        newRel.is_prime = res.response.is_prime;
        newRel.emergency_contact = res.response.emergency_contact;
        newRel.title = res.response.title;
        newRel.financial_responsible = res.response.financial_responsible;
        newRel.phi_authorized = res.response.phi_authorized;
        newRel.valid_until_revoked = res.response.valid_until_revoked;
        newRel.authorized_upto_date = res.response.authorized_upto_date;
        const updateRelation: Update<ContactRelationshipModel> = {
          id: newRel.id,
          changes: newRel,
        };
        this.store.dispatch(
          new ContactRelationshipOnServerUpdated({
            partialContactRelationship: updateRelation,
            contactRelationship: newRel,
            agreement: res.response.agreement.data,
          }),
        );
        this.loadRelsList();
        // add/revoke agreement here.
        const successMsg = this.translate.instant('CONTACT_RELATIONSHIP.EDIT.UPDATE_MESSAGE');
        this.layoutUtilsService.showActionNotification(
          successMsg,
          MessageType.Create,
          3000,
          true,
          false,
        );
      }
    });
  }

  /**
   * View Agreements
   *
   * @param _item
   */
  viewAgreementsButtonOnClick(_item: ContactRelationshipModel) {
    this.dialog.open(RelationshipAgreementsDialogComponent, {
      data: {
        entityId: _item.entity_id,
        contactId: this.contactId,
      },
      width: '1200px',
    });
  }

  getViewAgreementsButtonState(_item: ContactRelationshipModel): { disabled: boolean } {
    const disabled =
      this.isSwitchedToEditMode || _item.relationship_description.toUpperCase().trim() === 'SELF';

    return { disabled };
  }
}
